/**
 * `ProfilePanel` — the Profile panel of the Account view (ADR-0146 §1, §2).
 *
 * Two editable fields and one read-only:
 *
 *   - Account name (`account.name`)         — owner/admin only. Editable
 *                                              via explicit Save (a
 *                                              merchant-org rename should
 *                                              not blur-save on a phone).
 *   - Your name (`"user".name`)             — any role on OWN row.
 *                                              Explicit Save button.
 *   - Email (`"user".email`)                — READ-ONLY in Slice 1. A
 *                                              change-email flow needs a
 *                                              re-verify magic-link round
 *                                              trip (deferred).
 *
 * The two name fields use the SAME server-side validator
 * (`http/validators/display-name.ts`): trim, 1–80 chars, reject CR / LF
 * / NUL. The dashboard mirrors the length check so a user typing past
 * 80 sees the constraint before pressing Save; the server is the real
 * gate.
 *
 * Role gating: reviewer + viewer see the account name field as
 * READ-ONLY (the server's 403 on PATCH /v1/account is the real gate;
 * hiding the input is defense-in-depth). Their own user name stays
 * editable — every role can rename themselves.
 */

import { useCallback, useState } from 'react';

import type { AccountWire } from '@sill/types/api';
import type { User, UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAgency } from '../../app/agency-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import { TimeZoneSelect } from '../../components/TimeZoneSelect.js';
import { useTimeZoneContext } from '../../app/time-zone-context.js';
import { patchAccount } from '../../api/account.js';
import { patchMe } from '../../api/account-me.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const DISPLAY_NAME_MAX = 80;

export interface ProfilePanelProps {
  readonly account: AccountWire;
  readonly user: User;
  readonly role: UserRole;
  /** Called on a successful account.name PATCH. */
  readonly onAccountUpdated: (account: AccountWire) => void;
  /** Called on a successful me.name PATCH. */
  readonly onUserUpdated: (user: User) => void;
}

export function ProfilePanel({
  account,
  user,
  role,
  onAccountUpdated,
  onUserUpdated,
}: ProfilePanelProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const { refreshAccountTimeZone } = useTimeZoneContext();
  // Optional — test scaffolds that render ProfilePanel without an
  // AgencyProvider still work; acting is simply `null`.
  const agency = useOptionalAgency();
  const acting = agency?.acting ?? null;
  // ADR-0237 Slice 1 UX pass 2026-10-11 — the agency label is used
  // to disambiguate "Your profile" vs "{Client} account settings"
  // when the session is acting; both cards sit on the Account view
  // but represent different accounts.
  const client_label =
    acting !== null && account.name.trim().length > 0
      ? account.name.trim()
      : null;
  const agency_label =
    acting !== null && acting.home_account_name.trim().length > 0
      ? acting.home_account_name.trim()
      : null;

  const can_edit_account_name = role === 'owner' || role === 'admin';

  const [accountName, setAccountName] = useState(account.name);
  const [accountSaving, setAccountSaving] = useState(false);
  // ADR-0160 — account-wide default display zone; null = the UTC default.
  const [accountTz, setAccountTz] = useState<string | null>(
    account.display_time_zone ?? null,
  );
  const [accountTzSaving, setAccountTzSaving] = useState(false);
  const [userName, setUserName] = useState(user.name);
  const [userSaving, setUserSaving] = useState(false);

  const accountTzDirty = accountTz !== (account.display_time_zone ?? null);
  const accountDirty = accountName.trim() !== account.name.trim();
  const userDirty = userName.trim() !== user.name.trim();
  const accountValid = isLocallyValid(accountName);
  const userValid = isLocallyValid(userName);

  const saveAccountName = useCallback(async (): Promise<void> => {
    if (!can_edit_account_name || !accountDirty || !accountValid || http === null) {
      return;
    }
    setAccountSaving(true);
    const trimmed = accountName.trim();
    const result = await patchAccount(http, { name: trimmed });
    setAccountSaving(false);
    switch (result.kind) {
      case 'ok':
        onAccountUpdated(result.account);
        setAccountName(result.account.name);
        toast({ tone: 'lime', title: 'Account name saved.' });
        break;
      case 'forbidden':
        // Role demoted mid-session.
        setAccountName(account.name);
        toast({
          tone: 'coral',
          title: 'Your role cannot change the account name.',
        });
        break;
      case 'bad_request':
        toast({
          tone: 'coral',
          title: explainNameReason(result.reason),
        });
        break;
      case 'rate_limited':
        toast({ tone: 'amber', title: result.message });
        break;
      case 'error':
        toast({
          tone: 'coral',
          title: 'Could not save account name.',
          body: result.message,
        });
        break;
    }
  }, [
    can_edit_account_name,
    accountDirty,
    accountValid,
    http,
    accountName,
    account.name,
    onAccountUpdated,
    toast,
  ]);

  const saveAccountTimeZone = useCallback(async (): Promise<void> => {
    if (!can_edit_account_name || !accountTzDirty || http === null) return;
    setAccountTzSaving(true);
    // `null` clears the default → UTC. patchAccount accepts string | null.
    const result = await patchAccount(http, { display_time_zone: accountTz });
    setAccountTzSaving(false);
    switch (result.kind) {
      case 'ok':
        onAccountUpdated(result.account);
        setAccountTz(result.account.display_time_zone ?? null);
        // Tell the TimeZoneProvider to re-fetch so every dashboard date
        // reflects the new default without a reload.
        refreshAccountTimeZone();
        toast({ tone: 'lime', title: 'Default time zone saved.' });
        break;
      case 'forbidden':
        setAccountTz(account.display_time_zone ?? null);
        toast({ tone: 'coral', title: 'Your role cannot change the time zone.' });
        break;
      case 'bad_request':
        toast({ tone: 'coral', title: 'That time zone is not recognized.' });
        break;
      case 'rate_limited':
        toast({ tone: 'amber', title: result.message });
        break;
      case 'error':
        toast({
          tone: 'coral',
          title: 'Could not save the time zone.',
          body: result.message,
        });
        break;
    }
  }, [
    can_edit_account_name,
    accountTzDirty,
    http,
    accountTz,
    account.display_time_zone,
    onAccountUpdated,
    refreshAccountTimeZone,
    toast,
  ]);

  const saveUserName = useCallback(async (): Promise<void> => {
    if (!userDirty || !userValid || http === null) return;
    setUserSaving(true);
    const trimmed = userName.trim();
    const result = await patchMe(http, { name: trimmed });
    setUserSaving(false);
    switch (result.kind) {
      case 'ok':
        onUserUpdated(result.user);
        setUserName(result.user.name);
        toast({ tone: 'lime', title: 'Your name saved.' });
        break;
      case 'bad_request':
        toast({
          tone: 'coral',
          title: explainNameReason(result.reason),
        });
        break;
      case 'rate_limited':
        toast({ tone: 'amber', title: result.message });
        break;
      case 'error':
        toast({
          tone: 'coral',
          title: 'Could not save your name.',
          body: result.message,
        });
        break;
    }
  }, [
    userDirty,
    userValid,
    http,
    userName,
    onUserUpdated,
    toast,
  ]);

  return (
    <section id="profile">
    <Card className="p-5 sm:p-6">
      <div
        className="mb-5 pb-4"
        style={{ borderBottom: `1px solid ${C.border}` }}
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
          data-testid="profile-panel-kicker"
        >
          {client_label !== null ? `${client_label.toUpperCase()} · ACCOUNT` : 'PROFILE'}
        </div>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          {client_label !== null
            ? `${client_label} account settings`
            : 'What teammates and audit logs see'}
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          {client_label !== null
            ? `These settings belong to ${client_label}. Changes are audited as from your agency.`
            : 'Account name is the org label shown in emails and audit records. Your name is shown alongside actions you take.'}
        </div>
      </div>

      <Field
        label="Account name"
        hint={
          can_edit_account_name
            ? 'Shown to teammates and in audit logs.'
            : 'Only owners and admins can change the account name.'
        }
      >
        {can_edit_account_name ? (
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={accountName}
              maxLength={DISPLAY_NAME_MAX}
              disabled={accountSaving}
              onChange={(e) => setAccountName(e.target.value)}
              className="px-3 py-2 outline-none"
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                color: C.text,
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                flex: 1,
                minWidth: 200,
              }}
            />
            <button
              type="button"
              disabled={accountSaving || !accountDirty || !accountValid}
              onClick={() => void saveAccountName()}
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                letterSpacing: 1,
                color:
                  accountSaving || !accountDirty || !accountValid
                    ? C.muted
                    : C.ink,
                background:
                  accountSaving || !accountDirty || !accountValid
                    ? C.surface2
                    : C.lime,
                border: `1px solid ${
                  accountSaving || !accountDirty || !accountValid
                    ? C.border
                    : C.lime
                }`,
                borderRadius: 2,
                padding: '8px 14px',
                minHeight: 36,
                cursor:
                  accountSaving || !accountDirty || !accountValid
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {accountSaving ? 'SAVING…' : 'SAVE'}
            </button>
          </div>
        ) : (
          <ReadOnlyValue value={account.name} C={C} />
        )}
      </Field>

      {can_edit_account_name ? (
        <Field
          label="Default time zone"
          hint="How dates are shown across the dashboard. Sites can override this; audit exports stay UTC."
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div style={{ flex: 1, minWidth: 200 }}>
              <TimeZoneSelect
                aria-label="Default time zone"
                value={accountTz}
                onChange={setAccountTz}
                disabled={accountTzSaving}
                inheritLabel="UTC (default)"
              />
            </div>
            <button
              type="button"
              disabled={accountTzSaving || !accountTzDirty}
              onClick={() => void saveAccountTimeZone()}
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                letterSpacing: 1,
                color: accountTzSaving || !accountTzDirty ? C.muted : C.ink,
                background:
                  accountTzSaving || !accountTzDirty ? C.surface2 : C.lime,
                border: `1px solid ${
                  accountTzSaving || !accountTzDirty ? C.border : C.lime
                }`,
                borderRadius: 2,
                padding: '8px 14px',
                minHeight: 36,
                cursor:
                  accountTzSaving || !accountTzDirty ? 'not-allowed' : 'pointer',
              }}
            >
              {accountTzSaving ? 'SAVING…' : 'SAVE'}
            </button>
          </div>
        </Field>
      ) : null}

      {acting !== null ? (
        <div
          className="pt-5 pb-3"
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
          data-testid="profile-your-profile-divider"
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              marginBottom: 2,
            }}
          >
            {agency_label !== null ? `${agency_label.toUpperCase()} · YOU` : 'YOU'}
          </div>
          <div
            style={{
              fontFamily: serifFont,
              fontSize: 18,
              color: C.text,
              letterSpacing: -0.3,
              marginBottom: 2,
            }}
          >
            Your profile
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            Your own name and email on your agency. To change them, return to
            your agency first.
          </div>
        </div>
      ) : null}

      <Field label="Your name" hint="Shown alongside actions you take.">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={userName}
            maxLength={DISPLAY_NAME_MAX}
            disabled={userSaving || acting !== null}
            onChange={(e) => setUserName(e.target.value)}
            className="px-3 py-2 outline-none"
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              flex: 1,
              minWidth: 200,
            }}
          />
          <button
            type="button"
            disabled={acting !== null || userSaving || !userDirty || !userValid}
            onClick={() => void saveUserName()}
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              letterSpacing: 1,
              color:
                userSaving || !userDirty || !userValid ? C.muted : C.ink,
              background:
                userSaving || !userDirty || !userValid
                  ? C.surface2
                  : C.lime,
              border: `1px solid ${
                userSaving || !userDirty || !userValid ? C.border : C.lime
              }`,
              borderRadius: 2,
              padding: '8px 14px',
              minHeight: 36,
              cursor:
                userSaving || !userDirty || !userValid
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            {userSaving ? 'SAVING…' : 'SAVE'}
          </button>
        </div>
      </Field>

      <Field
        label={acting !== null ? 'Signed in as' : 'Your email'}
        hint={
          acting !== null
            ? `Your own login on ${agency_label ?? 'your agency'}, not the client's. The settings above belong to ${client_label ?? 'this client'}.`
            : 'Used for sign-in. Changing it is not available yet.'
        }
      >
        <ReadOnlyValue value={user.email} C={C} mono />
      </Field>
    </Card>
    </section>
  );
}

function isLocallyValid(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed.length < 1 || trimmed.length > DISPLAY_NAME_MAX) return false;
  if (/[\r\n\0]/.test(trimmed)) return false;
  return true;
}

function explainNameReason(reason: string | undefined): string {
  switch (reason) {
    case 'empty':
      return 'Name cannot be empty.';
    case 'too_long':
      return 'Name is too long (80 characters max).';
    case 'forbidden_chars':
      return 'Name cannot contain line breaks.';
    case 'not_string':
      return 'Name must be a string.';
    case 'role_insufficient':
      return 'Your role cannot change the account name.';
    default:
      return 'That name is not valid.';
  }
}

interface FieldProps {
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}

function Field({ label, hint, children }: FieldProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 py-4">
      <div className="sm:col-span-1">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: 'var(--theme-text)',
            fontWeight: 500,
          }}
        >
          {label}
        </div>
        {hint !== undefined ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: 'var(--theme-muted)',
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            {hint}
          </div>
        ) : null}
      </div>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}

interface ReadOnlyValueProps {
  readonly value: string;
  readonly C: { text: string; muted: string; border: string; surface2: string };
  readonly mono?: boolean;
}

function ReadOnlyValue({ value, C, mono }: ReadOnlyValueProps): JSX.Element {
  return (
    <div
      className="px-3 py-2"
      style={{
        fontFamily: mono === true ? monoFont : sansFont,
        fontSize: mono === true ? 13 : 14,
        color: C.text,
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      {value}
    </div>
  );
}
